import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ApiError, unwrap } from 'api/call'
import {
  getTweProductBrands,
  getTweProductCategories,
  getTweProductsById,
  getTweProductsByIdClicks,
  getTweProductsByIdDailyClicks,
  getTweProductsMostClicked,
  type TweBrand,
  type TweCategory,
  type TweDailyClicks,
  type TweMostClickedResponse,
  type TweProduct,
} from 'api/generated'
import { toast } from 'sonner'
import type {
  MostClickedFilters,
  MostClickedProduct,
  MostClickedSummary,
} from 'types/twe'

interface TweState {
  loading: boolean
  error: string | null
  products: MostClickedProduct[]
  summary: MostClickedSummary | null
  daily: TweDailyClicks
  categories: TweCategory[]
  brands: TweBrand[]
  product: TweProduct | null
  productClicks: number | null
  productLoading: boolean
  productError: string | null
  productDaily: TweDailyClicks | null
  productDailyLoading: boolean
}

const initialState: TweState = {
  loading: false,
  error: null,
  products: [],
  summary: null,
  daily: [],
  categories: [],
  brands: [],
  product: null,
  productClicks: null,
  productLoading: false,
  productError: null,
  productDaily: null,
  productDailyLoading: false,
}

export const fetchMostClicked = createAsyncThunk<
  TweMostClickedResponse,
  MostClickedFilters,
  { rejectValue: string }
>('twe/fetchMostClicked', async (filters, { rejectWithValue }) => {
  try {
    return await unwrap(getTweProductsMostClicked({ query: filters }))
  } catch {
    return rejectWithValue('Errore nel caricamento dei prodotti.')
  }
})

// ponytail: categories + brands are public, never change mid-session, fetched once on mount
export const fetchTweFilters = createAsyncThunk<{
  categories: TweCategory[]
  brands: TweBrand[]
}>('twe/fetchTweFilters', async () => {
  const [{ categories }, { brands }] = await Promise.all([
    unwrap(getTweProductCategories()),
    unwrap(getTweProductBrands()),
  ])
  return { categories, brands }
})

// Detail and click total are two endpoints; the page needs both to render once.
export const fetchTweProduct = createAsyncThunk<
  { product: TweProduct; clicks: number },
  number,
  { rejectValue: string }
>('twe/fetchTweProduct', async (id, { rejectWithValue }) => {
  try {
    const [product, { clicks_count }] = await Promise.all([
      unwrap(getTweProductsById({ path: { id } })),
      unwrap(getTweProductsByIdClicks({ path: { id } })),
    ])
    return { product, clicks: clicks_count }
  } catch (error) {
    // show() only serves available products, so a dropped feed row is a 404
    return rejectWithValue(
      error instanceof ApiError && error.status === 404
        ? 'Prodotto non trovato: non è più disponibile nel feed TWE.'
        : 'Errore nel caricamento del prodotto.'
    )
  }
})

export const fetchTweProductDailyClicks = createAsyncThunk<
  TweDailyClicks,
  { id: number; date_from?: string; date_to?: string }
>('twe/fetchTweProductDailyClicks', async ({ id, ...query }) => {
  const { daily } = await unwrap(
    getTweProductsByIdDailyClicks({ path: { id }, query })
  )
  return daily
})

export const tweSlice = createSlice({
  name: 'twe',
  initialState,
  reducers: {
    clearTweProduct: (state) => {
      state.product = null
      state.productClicks = null
      state.productError = null
      state.productDaily = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMostClicked.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchMostClicked.fulfilled, (state, action) => {
      state.loading = false
      state.products = action.payload.products
      state.summary = action.payload.summary ?? null
      state.daily = action.payload.daily
    })
    builder.addCase(fetchMostClicked.rejected, (state, action) => {
      state.loading = false
      state.summary = null
      state.daily = []
      state.error = action.payload || 'Impossibile caricare i dati'
      toast.error('Errore nel caricamento dei prodotti più cliccati')
    })
    builder.addCase(fetchTweFilters.fulfilled, (state, action) => {
      state.categories = action.payload.categories
      state.brands = action.payload.brands
    })
    builder.addCase(fetchTweFilters.rejected, () => {
      toast.error('Errore nel caricamento dei filtri')
    })
    builder.addCase(fetchTweProduct.pending, (state) => {
      state.productLoading = true
      state.productError = null
    })
    builder.addCase(fetchTweProduct.fulfilled, (state, action) => {
      state.productLoading = false
      state.product = action.payload.product
      state.productClicks = action.payload.clicks
    })
    builder.addCase(fetchTweProduct.rejected, (state, action) => {
      state.productLoading = false
      state.product = null
      state.productError = action.payload || 'Impossibile caricare il prodotto'
    })
    builder.addCase(fetchTweProductDailyClicks.pending, (state) => {
      state.productDailyLoading = true
    })
    builder.addCase(fetchTweProductDailyClicks.fulfilled, (state, action) => {
      state.productDailyLoading = false
      state.productDaily = action.payload
    })
    builder.addCase(fetchTweProductDailyClicks.rejected, (state) => {
      state.productDailyLoading = false
      state.productDaily = null
      toast.error('Errore nel caricamento dei click per giorno')
    })
  },
})

export const { clearTweProduct } = tweSlice.actions
export const tweReducer = tweSlice.reducer
